import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { ArrowLeftOutlined, ArrowRightOutlined } from '@mui/icons-material';
import { Box, Fade } from '@mui/material';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useDispatch } from 'react-redux';

import { AnalyticsService, EVENTS } from '@/services';
import {
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsPdfPageControlsGlowDismissed,
  useAppSelector,
  useFetchEpisodeQuery,
  userPreferencesActions,
} from '@/store';
import { useLegacyCDNHost } from '@/store/store.utils';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export const VisualAidViewer = () => {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [isPdfFocused, setIsPdfFocused] = useState(false);
  const [pdfContainerWidth, setPdfContainerWidth] = useState(0);
  const [pdfContainerHeight, setPdfContainerHeight] = useState(0);

  const activeEpisodeId = useAppSelector(selectActiveEpisodeId)!;
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );
  const { data: episode } = useFetchEpisodeQuery({
    episodeId: activeEpisodeId,
    compliance,
  });

  const isPdfPageControlsGlowDismissed = useAppSelector(
    selectIsPdfPageControlsGlowDismissed,
  );

  const shouldGlowPdfPageControls = !isPdfPageControlsGlowDismissed;

  const handleResize = useEffectEvent(() => {
    if (!pdfContainerRef.current) return;
    const containerWidth = pdfContainerRef.current.offsetWidth;
    const containerHeight = pdfContainerRef.current.offsetHeight;
    setPdfContainerWidth(containerWidth);
    setPdfContainerHeight(containerHeight);
  });

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    episode?.visualAid?.horizontalPDF && (
      <Box
        ref={pdfContainerRef}
        onMouseEnter={() => {
          setIsPdfFocused(true);
          dispatch(userPreferencesActions.dismissPdfPageControlsGlow());
          AnalyticsService.logEvent(
            EVENTS.VISUAL_LEARNING_EVENTS.VISUAL_AID_HOVER,
          );
        }}
        onMouseLeave={() => {
          setIsPdfFocused(false);
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          aspectRatio: '16/9',
        }}>
        <Document
          file={
            useLegacyCDNHost()
              ? `https://ipa.lumiqlearn.com/bff/files/proxy?url=${episode?.visualAid?.horizontalPDF?.url}`
              : episode?.visualAid?.horizontalPDF?.url
          }
          onLoadSuccess={({ numPages }) => setPageCount(numPages)}>
          {Array.from(Array(pageCount)).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: index + 1 === pageNumber ? 'block' : 'none',
              }}>
              <Page pageNumber={index + 1} width={pdfContainerWidth} />
            </Box>
          ))}
        </Document>
        <Fade in={isPdfFocused || shouldGlowPdfPageControls}>
          <Box
            sx={[
              styles.pdfPageControl,
              { right: 16, top: pdfContainerHeight / 2 },
              shouldGlowPdfPageControls && styles.pdfPageControlGlow,
            ]}>
            <ArrowRightOutlined
              sx={{
                width: 50,
                height: 50,
              }}
              onClick={() => {
                setPageNumber(prev => Math.min(prev + 1, pageCount));
                AnalyticsService.logEvent(
                  EVENTS.VISUAL_LEARNING_EVENTS.NEXT_PAGE,
                  {
                    pageNumber: pageNumber + 1,
                    pageCount,
                  },
                );
              }}
            />
          </Box>
        </Fade>
        <Fade in={isPdfFocused || shouldGlowPdfPageControls}>
          <Box
            sx={[
              styles.pdfPageControl,
              { left: 16, top: pdfContainerHeight / 2 },
              shouldGlowPdfPageControls && styles.pdfPageControlGlow,
            ]}>
            <ArrowLeftOutlined
              sx={{
                width: 50,
                height: 50,
              }}
              onClick={() => {
                setPageNumber(prev => Math.max(prev - 1, 1));
                AnalyticsService.logEvent(
                  EVENTS.VISUAL_LEARNING_EVENTS.PREVIOUS_PAGE,
                  {
                    pageNumber: pageNumber - 1,
                    pageCount,
                  },
                );
              }}
            />
          </Box>
        </Fade>
      </Box>
    )
  );
};

const styles = {
  pdfPageControl: {
    position: 'absolute',
    transform: 'translateY(-50%)',
    display: 'flex',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    cursor: 'pointer',
    zIndex: 'overlay',
  },
  pdfPageControlGlow: {
    opacity: 0,
    animation: 'pdfPageControlGlowOpacity 1.2s ease-in-out infinite',
    '@keyframes pdfPageControlGlowOpacity': {
      '0%': {
        opacity: 0,
      },
      '50%': {
        opacity: 1,
      },
      '100%': {
        opacity: 0,
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 1,
    },
  },
};
